import { minimatch } from 'minimatch';
import { IEditors, ITreeItem, editors, extensions } from './types';
import { AppState } from '..';
import { FileFolderType, INodeInfo, MODEL_TAB_PATH, ITab } from '@qodly/shared';

export function getNodeRelativePath(nodePath: string): string {
  const chunks = nodePath.split('/');

  chunks.pop();

  return chunks.join('/');
}

export function getEditors(path: string): IEditors {
  const editor = editors.find(({ pattern }) =>
    minimatch(path, pattern, {
      nocase: true,
    }),
  );

  if (editor) {
    return editor;
  }

  return {
    pattern: '',
    editor: {
      key: 'editors:unknown',
      config: {
        language: 'text',
      },
    },
  };
}

export function getExtension(path: string) {
  const extension = extensions.find(({ pattern }) =>
    minimatch(path, pattern, {
      nocase: true,
    }),
  );

  if (extension) {
    return extension.type;
  }

  return 'text';
}

type TGetContentParams = {
  name: string;
  qodly: boolean;
};

type TGetContentReturnValue = {
  [key: string]: (params: TGetContentParams) => {
    name: string;
    content: string;
  };
};

export const getContent = (): TGetContentReturnValue => {
  return {
    dataclass: ({ name, qodly }) => {
      return {
        name,
        content: qodly ? 'extends DataClass\n' : 'Class extends DataClass\n',
      };
    },
    collection: ({ name, qodly }) => {
      return {
        name: `${name}Selection`,
        content: qodly
          ? 'extends EntitySelection\n'
          : 'Class extends EntitySelection\n',
      };
    },
    entity: ({ name, qodly }) => {
      return {
        name: `${name}Entity`,
        content: qodly ? 'extends Entity\n' : 'Class extends Entity\n',
      };
    },
    model: ({ name, qodly }) => {
      return {
        name,
        content: qodly
          ? 'extends DataStoreImplementation\n'
          : 'Class extends DataStoreImplementation\n',
      };
    },
    app: ({ name }) => {
      return {
        name,
        content:
          'shared singleton constructor()\n\nshared function onStartup()\n\nshared function onStop()',
      };
    },
  };
};

export const createClassesFolderContent = async (
  state: AppState,
  children: INodeInfo[],
  expandDataclass?: string,
): Promise<ITreeItem[]> => {
  if (state.catalog.state !== 'loaded') return [];
  const {
    settings: {
      server: { qodly },
    },
  } = state;

  const defaultExt = qodly ? '4qs' : '4dm';
  const modelTab = state.root.tabs.find(
    (tab: ITab) => tab.path === MODEL_TAB_PATH,
  );
  const catalogDataclasses = state.catalog.model.dataClasses;
  const dataClasses = Object.values(
    modelTab
      ? (modelTab.content.dataClasses as datasources.IDataClasses)
      : catalogDataclasses,
  );
  const lastClassesState = state.explorer.find(
    (node) => node.name === 'Classes',
  );

  // search for the DataStore.
  const foundDataStore = (children as INodeInfo[]).find(
    ({ name }) => name === 'DataStore',
  );
  const datastorePath = `Classes/DataStore.${
    foundDataStore?.ext || defaultExt
  }`;
  const config = getEditors(datastorePath);
  const datastore = {
    ext: defaultExt,
    ...foundDataStore,
    name: 'DataStore',
    type: FileFolderType.CLASS,
    path: datastorePath,
    error: null,
    flags: { expanded: false, loading: false, cached: false },
    config,
    info: {
      kind: 'model',
      filename: 'DataStore',
      exist: !!foundDataStore,
    },
  };

  // search for the QodlyEvents
  const qodlyEvents = [];
  if (qodly) {
    const foundQodlyEvents = (children as INodeInfo[]).find(
      ({ name }) => name === 'QodlyEvents',
    );
    qodlyEvents.push({
      ext: defaultExt,
      ...foundQodlyEvents,
      name: 'QodlyEvents',
      type: FileFolderType.CLASS,
      path: `Classes/QodlyEvents.${defaultExt}`,
      error: null,
      flags: { expanded: false, loading: false, cached: false },
      config,
      info: {
        kind: 'app',
        filename: 'QodlyEvents',
        exist: !!foundQodlyEvents,
      },
    });
  }

  // to map dataClasses.
  const classesList = dataClasses.map<ITreeItem & { uuid?: string }>(
    ({ name, className, collectionName, uuid }) => {
      const foundDataClass = (children as INodeInfo[]).find(
        ({ name: childName }) => childName === name,
      );
      const foundEntity = (children as INodeInfo[]).find(
        ({ name: childName }) => childName === `${className}Entity`,
      );
      const foundSelection = (children as INodeInfo[]).find(
        ({ name: childName }) => childName === collectionName,
      );

      //create the node
      const lastRootNode = lastClassesState?.children?.find(
        (child) => child.path === `Classes/${name}___[<QODLY_ID>]`,
      );
      const dataclassPath = `Classes/${name}.${
        foundDataClass?.ext || defaultExt
      }`;
      const entityPath = `Classes/${name}Entity.${
        foundEntity?.ext || defaultExt
      }`;
      const selectionPath = `Classes/${collectionName}.${
        foundSelection?.ext || defaultExt
      }`;

      return {
        name: name,
        type: FileFolderType.CLASS_FOLDER,
        children: [
          // datastore classes
          {
            ext: defaultExt,
            ...foundDataClass,
            name: 'DataClass',
            type: FileFolderType.CLASS,
            path: dataclassPath,
            error: null,
            flags: {},
            config: getEditors(dataclassPath),
            info: {
              dataclass: name,
              filename: name,
              kind: 'dataclass',
              exist: !!foundDataClass,
            },
          },
          {
            ext: defaultExt,
            ...foundEntity,
            name: 'Entity',
            type: FileFolderType.CLASS,
            path: entityPath,
            error: null,
            flags: {},
            config: getEditors(entityPath),
            info: {
              dataclass: name,
              filename: `${name}Entity`,
              kind: 'entity',
              exist: !!foundEntity,
            },
          },
          {
            ext: defaultExt,
            ...foundSelection,
            name: 'Selection',
            type: FileFolderType.CLASS,
            path: selectionPath,
            error: null,
            flags: {},
            config: getEditors(selectionPath),
            info: {
              dataclass: name,
              filename: `${name}Selection`,
              kind: 'collection',
              exist: !!foundSelection,
            },
          },
        ],
        path: `Classes/${name}___[<QODLY_ID>]`,
        error: null,
        flags: {
          expanded:
            lastRootNode?.flags.expanded || expandDataclass === name || false,
          loading: false,
          cached: false,
        },
        config: {},
        info: {
          uuid,
        },
      };
    },
  );

  //the rest of classes.
  const userClasses = (children as INodeInfo[])
    .filter((child) => {
      if (!child.name) return;

      const isDatastore = child.name === 'DataStore';
      const isQodlyEvents = child.name === 'QodlyEvents';
      const isDataclass = dataClasses.find(({ name }) => name === child.name);
      const isEntity = dataClasses.find(
        ({ name }) => `${name}Entity` === child.name,
      );
      const isSelection = dataClasses.find(
        ({ name }) => `${name}Selection` === child.name,
      );

      return (
        !isQodlyEvents &&
        !isDataclass &&
        !isDatastore &&
        !isEntity &&
        !isSelection
      );
    })
    .map((userClass) => {
      const path = `Classes/${userClass.name}.${userClass?.ext || defaultExt}`;
      return {
        ...userClass,
        type: FileFolderType.CLASS,
        path,
        error: null,
        flags: { expanded: false, loading: false, cached: false },
        config: getEditors(path),
        info: {
          filename: userClass.name,
          kind: 'userClass',
          exist: true,
        },
      };
    });

  // all Classes files ordrer following => [datastore, dataclasses, user classes]
  return [...qodlyEvents, datastore, ...classesList, ...userClasses];
};

export const getFileNameWithoutExtension = (fileName: string) => {
  return fileName.split('.').slice(0, -1).join('.');
};
